import { ForwardedRef, forwardRef, MouseEvent, ReactNode, useCallback, useEffect, useRef } from 'react'

import { TableCell, Skeleton, Checkbox, useTheme } from '@mui/material'
import { Cell, flexRender, Row, Table } from '@tanstack/react-table'
import { AnimatePresence, motion, useAnimate } from 'motion/react'

import { AnimatedButton, AnimatedButtonProps } from '@shared/components/ContextMenu'
import {
  ActionMenuTableCell,
  CheckboxTableCell,
  DynamicContentTableCell,
  RowProps,
  BodyTableRow as StyledRow,
  SubRow,
  subRowVariants,
  getRowBackgroundColor,
} from '@shared/components/Table'

const AnimatedRow = motion.create(
  forwardRef(function RefForwardedRow(props: RowProps, ref: ForwardedRef<HTMLTableRowElement>) {
    return <StyledRow ref={ref} {...props} />
  }),
)

const AnimatedSubRow = motion.create(
  forwardRef(function RefForwardedRow(props: RowProps, ref: ForwardedRef<HTMLTableRowElement>) {
    return <SubRow ref={ref} {...props} />
  }),
)

type BodyRowProps<D> = {
  tableInstance: Table<D>
  row: Row<D>
  enableSelection?: boolean
  enableLinking?: boolean
  LinkProps?: (row: Row<D>, content: ReactNode, cell: Cell<D, unknown>) => ReactNode
  excludeFieldsFromLinking?: string[]
  enableClickAction?: boolean
  clickAction?: (row: Row<D>) => void
  columnWidth: {
    gridTemplateColumns: string
  }
  isInitialLoading?: boolean
  isInitialLoadingByRowByID?: {
    [key: string]: string[]
  }
  AnimatedButtonProps?: AnimatedButtonProps
  renderSubComponent?: ({ row }: { row: Row<D> }) => ReactNode
  tint?: string
}

const BodyRowRaw = <D,>({
  tableInstance,
  row,
  enableSelection,
  enableLinking,
  LinkProps,
  excludeFieldsFromLinking,
  enableClickAction,
  clickAction,
  columnWidth,
  isInitialLoadingByRowByID,
  AnimatedButtonProps,
  isInitialLoading,
  renderSubComponent,
  tint,
}: BodyRowProps<D>) => {
  const theme = useTheme()
  const [scope, animate] = useAnimate()
  const isHovered = useRef(false)

  const isSelected = !isInitialLoading && row.getIsSelected()

  const handleSelection = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const selectedRows = tableInstance.getSelectedRowModel().rows
      if (e.shiftKey) {
        if (selectedRows.length) {
          const firstAlreadySelectedRow = selectedRows[0]
          const lastAlreadySelectedRow = [...selectedRows].reverse()[0]

          if (row.index <= firstAlreadySelectedRow.index) {
            tableInstance
              .getRowModel()
              .rows.slice(row.index, lastAlreadySelectedRow.index + 1)
              .forEach((item) => item.toggleSelected(true))
          } else if (row.index >= lastAlreadySelectedRow.index) {
            tableInstance
              .getRowModel()
              .rows.slice(firstAlreadySelectedRow.index, row.index + 1)
              .forEach((item) => item.toggleSelected(true))
          }
        }
      } else {
        row.toggleSelected()
      }
    },
    [row, tableInstance],
  )

  const handleClickAction = useCallback(() => {
    if (clickAction) {
      clickAction(row)
    }
  }, [clickAction, row])

  const onHoverStart = useCallback(() => {
    isHovered.current = true
    if (scope.current) {
      animate(scope.current, { backgroundColor: getRowBackgroundColor({ theme, isSelected, isHovered: true, tint }) })
    }
  }, [animate, isSelected, scope, theme, tint])

  const onHoverEnd = useCallback(() => {
    isHovered.current = false
    if (scope.current) {
      animate(scope.current, { backgroundColor: getRowBackgroundColor({ theme, isSelected, tint }) })
    }
  }, [animate, isSelected, scope, theme, tint])

  useEffect(() => {
    animate(
      scope.current,
      { backgroundColor: getRowBackgroundColor({ theme, isSelected, isHovered: isHovered.current, tint }) },
      { duration: 0.01 },
    )
  }, [animate, isSelected, scope, theme, tint])

  const hasLoadingRows = isInitialLoadingByRowByID?.[row.id]

  return (
    <>
      <AnimatedRow
        ref={scope}
        columnWidth={columnWidth}
        height={tableInstance.options.meta?.sizing.height}
        isIndex={row.index !== [...tableInstance.getRowModel().rows].reverse()[0].index}
        onHoverStart={onHoverStart}
        onHoverEnd={onHoverEnd}
      >
        {enableSelection && (
          <CheckboxTableCell isSelected={isSelected} height={tableInstance.options.meta?.sizing.height}>
            <Checkbox
              size="small"
              checked={isSelected}
              onClick={handleSelection}
              disabled={hasLoadingRows && hasLoadingRows.length === 0}
            />
          </CheckboxTableCell>
        )}
        {row.getVisibleCells().map((cell) => (
          <DynamicContentTableCell
            key={cell.id}
            component="td"
            height={tableInstance.options.meta?.sizing.height}
            horizontalAlignment={cell.column.columnDef.meta?.align}
            horizontalPadding={cell.column.columnDef.meta?.sizing?.px}
            isLinkCell={!!(enableLinking || enableClickAction) && !excludeFieldsFromLinking?.includes(cell.column.id)}
            {...(enableClickAction &&
              clickAction &&
              !excludeFieldsFromLinking?.includes(cell.column.id) &&
              !hasLoadingRows && {
                onClick: handleClickAction,
              })}
          >
            {isInitialLoading ||
            !!(hasLoadingRows && hasLoadingRows.length === 0) ||
            hasLoadingRows?.includes(cell.column.id) ? (
              <Skeleton sx={{ width: '100%' }} />
            ) : enableLinking && !!LinkProps && !excludeFieldsFromLinking?.includes(cell.column.id) ? (
              LinkProps(row, flexRender(cell.column.columnDef.cell, cell.getContext()), cell)
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </DynamicContentTableCell>
        ))}
        {!!AnimatedButtonProps && (
          <ActionMenuTableCell height={tableInstance.options.meta?.sizing.height}>
            {!isInitialLoading && (
              <AnimatedButton
                {...AnimatedButtonProps}
                IconButtonProps={{
                  ...AnimatedButtonProps.IconButtonProps,
                  disabled: hasLoadingRows && hasLoadingRows.length === 0,
                }}
              />
            )}
          </ActionMenuTableCell>
        )}
      </AnimatedRow>
      {!!renderSubComponent && (
        <AnimatePresence mode="wait">
          {row.getIsExpanded() && (
            <AnimatedSubRow
              key={`${row.id}-expansion`}
              variants={subRowVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <TableCell colSpan={row.getVisibleCells().length}>{renderSubComponent({ row })}</TableCell>
            </AnimatedSubRow>
          )}
        </AnimatePresence>
      )}
    </>
  )
}

export const BodyRow = BodyRowRaw
